import {
    afterNextRender,
    DestroyRef,
    Directive,
    ElementRef,
    type OnInit,
    booleanAttribute,
    inject,
    input,
    signal,
} from '@angular/core';

const CONTENT_MAX_WIDTH_REM = 80;
const CONTENT_EXPANSION_THRESHOLD_PX = 1280;
const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    'a[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
const FOCUS_FALLBACK_SELECTOR = '[data-qpx-content-width-focus-fallback]';
const WIDTH_SENSITIVE_SELECTOR = '[data-qpx-content-width-sensitive]';

@Directive({
    selector: '[qpxContentWidth]',
    host: {
        '[style.--qpx-content-max-width]':
            'expanded() ? "none" : collapsedMaxWidth',
        '[style.--qpx-content-width-toggle-display]':
            'hasExpandableWidth() ? "contents" : "none"',
        '[style.--qpx-pagination-boundary-display]':
            'hasExpandableWidth() ? "inline-flex" : "none"',
    },
    exportAs: 'qpxContentWidth',
})
export class QpxContentWidthContainer implements OnInit {
    //#region Dependency injection
    private readonly destroyRef = inject(DestroyRef);
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    //#endregion

    //#region Constants
    protected readonly collapsedMaxWidth = `${CONTENT_MAX_WIDTH_REM}rem`;
    //#endregion

    //#region Inputs
    public readonly initialExpanded = input(false, {
        transform: booleanAttribute,
    });
    //#endregion

    //#region State
    public readonly expanded = signal(false);

    protected readonly hasExpandableWidth = signal(false);
    //#endregion

    constructor() {
        afterNextRender({
            read: () => this.observeContentWidth(),
        });
    }

    //#region Lifecycle hooks
    public ngOnInit(): void {
        this.expanded.set(this.initialExpanded());
    }
    //#endregion

    //#region Public methods
    public toggle(): void {
        this.expanded.update((value) => !value);
    }

    public expand(): void {
        this.expanded.set(true);
    }

    public collapse(): void {
        this.expanded.set(false);
    }
    //#endregion

    //#region Private methods
    private observeContentWidth(): void {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const element = this.elementRef.nativeElement;
        const observer = new ResizeObserver(([entry]) => {
            if (!entry) {
                return;
            }

            const width =
                entry.contentBoxSize[0]?.inlineSize ?? entry.contentRect.width;
            const hasExpandableWidth = width > CONTENT_EXPANSION_THRESHOLD_PX;

            if (hasExpandableWidth !== this.hasExpandableWidth()) {
                if (!hasExpandableWidth) {
                    this.preserveFocusBeforeHiding(element);
                }
                this.hasExpandableWidth.set(hasExpandableWidth);
            }
        });

        observer.observe(element, { box: 'content-box' });
        this.destroyRef.onDestroy(() => observer.disconnect());
    }

    private preserveFocusBeforeHiding(element: HTMLElement): void {
        const activeElement = element.ownerDocument.activeElement;
        if (
            !(activeElement instanceof HTMLElement) ||
            !element.contains(activeElement) ||
            !activeElement.closest(WIDTH_SENSITIVE_SELECTOR)
        ) {
            return;
        }

        const scope = activeElement.closest('qpx-pagination') ?? element;
        const fallback =
            this.findFocusFallback(scope, activeElement) ??
            scope.querySelector<HTMLElement>(FOCUS_FALLBACK_SELECTOR) ??
            (scope === element
                ? undefined
                : this.findFocusFallback(element, activeElement));

        fallback?.focus({ preventScroll: true });
    }

    private findFocusFallback(
        scope: Element,
        activeElement: HTMLElement
    ): HTMLElement | undefined {
        const focusableElements = Array.from(
            scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        );
        const activeIndex = focusableElements.findIndex(
            (candidate) =>
                candidate === activeElement || candidate.contains(activeElement)
        );
        if (activeIndex === -1) {
            return undefined;
        }

        const candidates = [
            ...focusableElements.slice(activeIndex + 1),
            ...focusableElements.slice(0, activeIndex).reverse(),
        ];
        return candidates.find(
            (candidate) =>
                !candidate.closest(WIDTH_SENSITIVE_SELECTOR) &&
                candidate.getAttribute('aria-disabled') !== 'true' &&
                this.isVisible(candidate)
        );
    }

    private isVisible(element: HTMLElement): boolean {
        const style =
            element.ownerDocument.defaultView?.getComputedStyle(element);

        return style?.display !== 'none' && style?.visibility !== 'hidden';
    }
    //#endregion
}
